'use client';

import { useCallback, useSyncExternalStore } from 'react';

/**
 * SSR-safe media query subscription. The server snapshot is always false, so the
 * first client render matches server HTML and the real value applies right after
 * hydration without a mismatch.
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      if (typeof window === 'undefined' || !window.matchMedia) return () => {};
      const mql = window.matchMedia(query);
      mql.addEventListener('change', onStoreChange);
      return () => mql.removeEventListener('change', onStoreChange);
    },
    [query]
  );

  const getSnapshot = useCallback(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  }, [query]);

  return useSyncExternalStore(subscribe, getSnapshot, () => false);
}

export type ShellViewport = 'mobile' | 'desktop' | 'wide';

/**
 * The shell's three responsive tiers: mobile (<1024px, drawers), desktop
 * (1024-1439px, rail as slide-over inspector), wide (>=1440px, rail pinnable).
 */
export function useShellViewport(): ShellViewport {
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const isWide = useMediaQuery('(min-width: 1440px)');
  if (isWide) return 'wide';
  if (isDesktop) return 'desktop';
  return 'mobile';
}
